/*
 * font-converter/index.js — the page.
 *
 * Structure: everything that costs CPU lives in worker.js; everything that
 * needs the DOM lives here. The one deliberate exception is the render proof
 * (verify.js), which needs FontFace and canvas and therefore cannot leave the
 * main thread — it runs after the bytes are already produced, on a font the
 * browser has to be able to load anyway.
 */

import { pack } from '../file-converter/zip-out.js';
import { renderProof, loadable } from './verify.js';
import { scriptCoverage, languageCoverage, subsetAvailability, SUBSETS, rangesToCss, intersectRanges, normalizeRanges } from './ranges.js';
import { buildStylesheet, buildDemoHtml, suggestFamilyName } from './css.js';

const CFG = window.__FONTCONV_CFG || {};
const T = window.__FC_FONT_I18N || {};
const t = (k, vars) => {
    let s = T[k] || k;
    /* Longest key first: ':n' is a prefix of ':name' and st_converting uses both.
       Today the call site happens to list name before n, so it works by luck —
       reordering that object literal would silently corrupt the label. Sorting
       here removes the luck. */
    if (vars) for (const key of Object.keys(vars).sort((a, b) => b.length - a.length)) s = s.split(':' + key).join(vars[key]);
    return s;
};

const $ = (id) => document.getElementById(id);
const el = (tag, cls, text) => {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text !== undefined) n.textContent = text;
    return n;
};

const S = {
    files: [],
    sel: 0,
    worker: null,
    seq: 0,
    pending: new Map(),
    busy: false,
    /* Throughput of the WOFF2 encoder on THIS device, KB of source per second.
     * Seeded from a desktop measurement and replaced by the first real
     * conversion — a static estimate on a phone is off by 3-5x, and a progress
     * bar that lies is worse than one that says nothing. */
    kbps: Number(localStorage.getItem('fc_woff2_kbps')) || 130,
};

/* ─────────────────────────── worker plumbing ─────────────────────────── */

function worker() {
    if (S.worker) return S.worker;
    /* An engine without module workers (Firefox < 114, Safari < 15) starts the
     * converter as a classic script and it dies on its first import. Measured on an
     * emulated engine (08.10.2026): the font never showed, the button did nothing,
     * and the only word was the shared «something went wrong» toast. The engine is
     * asked first; the card says what is true (err_module_worker_unsupported). */
    if (window.__moduleWorkerOk && !window.__moduleWorkerOk()) {
        throw Object.assign(new Error('module_worker_unsupported'), { code: 'MODULE_WORKER_UNSUPPORTED' });
    }
    const w = new Worker(CFG.workerUrl, { type: 'module' });
    w.addEventListener('message', (ev) => {
        const m = ev.data || {};
        const p = S.pending.get(m.id);
        if (m.type === 'progress') { if (p && p.onProgress) p.onProgress(m.stage, m.ratio); return; }
        if (!p) return;
        if (m.type === 'error') { S.pending.delete(m.id); p.reject(Object.assign(new Error(m.message), { code: m.code })); return; }
        S.pending.delete(m.id);
        p.resolve(m);
    });
    w.addEventListener('error', (e) => {
        // The failure is taken here: every waiting call is answered (before, they waited
        // forever) and the card says the conversion failed. Uncancelled, the same error
        // would also reach window.onerror — the shared toast, Sentry.
        if (e && e.preventDefault) e.preventDefault();
        for (const p of S.pending.values()) p.reject(Object.assign(new Error((e && e.message) || 'worker failed'), { code: 'CONVERT_FAILED' }));
        S.pending.clear();
        if (S.worker === w) S.worker = null;
        try { w.terminate(); } catch (_) {}
        try { window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'font_worker_failed', msg: String((e && e.message) || '').slice(0, 120) }); } catch (_) {}
    });
    w.postMessage({ type: 'config', id: 0, cfg: CFG.vendor });
    S.worker = w;
    return w;
}

function ask(type, payload, onProgress, transfer) {
    const id = ++S.seq;
    return new Promise((resolve, reject) => {
        S.pending.set(id, { resolve, reject, onProgress });
        try { worker().postMessage(Object.assign({ type, id }, payload), transfer || []); }
        catch (e) { S.pending.delete(id); reject(e); }
    });
}

/* ─────────────────────────── formatting ─────────────────────────── */

const fmtBytes = (n) => {
    if (n < 1024) return n + ' ' + t('unit_b');
    if (n < 1024 * 1024) return (n / 1024).toFixed(n < 10240 ? 1 : 0) + ' ' + t('unit_kb');
    return (n / 1048576).toFixed(2) + ' ' + t('unit_mb');
};
const pct = (v) => (v * 100).toFixed(v < 0.1 ? 1 : 0) + '%';

function delta(before, after) {
    if (!before || after >= before) return '';
    return '−' + Math.round((1 - after / before) * 100) + '%';
}

/* ─────────────────────────── file intake ─────────────────────────── */

async function addFiles(list) {
    const files = [].slice.call(list || []);
    for (const f of files) {
        if (S.files.length >= 24) { window.__toast(t('err_too_many'), 'warning'); break; }
        const bytes = new Uint8Array(await f.arrayBuffer());
        const rec = { id: 'f' + (++S.seq), name: f.name, size: f.size, bytes, faceIndex: 0, insp: null, result: null };
        S.files.push(rec);
        try {
            const r = await ask('inspect', { bytes: bytes.buffer.slice(0), name: f.name });
            rec.insp = r.info;
        } catch (e) {
            rec.error = e.code || 'NOT_A_FONT';
        }
    }
    S.sel = S.files.length - 1;
    window.__stage && window.__stage('font_loaded');
    render();
}

/* ─────────────────────────── render: file list ─────────────────────────── */

function renderList() {
    const box = $('fxList');
    box.innerHTML = '';
    box.hidden = S.files.length < 2;
    S.files.forEach((f, i) => {
        const b = el('button', 'fx-tab' + (i === S.sel ? ' is-on' : ''));
        b.type = 'button';
        b.appendChild(el('span', 'fx-tab-name', f.insp && !f.insp.collection ? f.insp.info.names.family || f.name : f.name));
        b.appendChild(el('span', 'fx-tab-size', fmtBytes(f.size)));
        if (f.error) b.classList.add('is-bad');
        b.addEventListener('click', () => { S.sel = i; render(); });
        box.appendChild(b);
    });
}

/* ─────────────────────────── render: identity card ─────────────────────────── */

function chip(text, kind) {
    const c = el('span', 'fx-chip' + (kind ? ' fx-chip-' + kind : ''), text);
    return c;
}

function renderCard(f) {
    const card = $('fxCard');
    if (!f || f.error) {
        card.hidden = true;
        if (f && f.error) {
            $('fxError').hidden = false;
            $('fxError').textContent = t('err_' + f.error.toLowerCase()) !== 'err_' + f.error.toLowerCase()
                ? t('err_' + f.error.toLowerCase()) : t('err_not_a_font');
        }
        return;
    }
    $('fxError').hidden = true;
    card.hidden = false;

    // A collection asks which face BEFORE anything else — every other panel
    // describes one face, so guessing here would describe the wrong font.
    const faces = $('fxFaces');
    faces.innerHTML = '';
    if (f.insp.collection) {
        faces.hidden = false;
        faces.appendChild(el('span', 'fx-faces-lbl', t('collection_pick', { n: f.insp.faces.length })));
        f.insp.faces.forEach((face) => {
            const b = el('button', 'fx-face' + (face.index === f.faceIndex ? ' is-on' : ''));
            b.type = 'button';
            b.textContent = face.info ? ((face.info.names.family || '') + ' ' + (face.info.names.subfamily || '')).trim() : '#' + face.index;
            b.addEventListener('click', async () => {
                f.faceIndex = face.index;
                render();
            });
            faces.appendChild(b);
        });
    } else {
        faces.hidden = true;
    }

    const info = faceInfo(f);
    if (!info) return;

    $('fxName').textContent = info.names.family || f.name;
    const sub = [];
    if (info.names.subfamily) sub.push(info.names.subfamily);
    if (info.names.version) sub.push(info.names.version.replace(/^Version\s*/i, 'v'));
    if (info.names.designer) sub.push(info.names.designer);
    $('fxSub').textContent = sub.join(' · ');

    const chips = $('fxChips');
    chips.innerHTML = '';
    chips.appendChild(chip(t('n_glyphs', { n: info.glyphs })));
    chips.appendChild(chip(t('n_chars', { n: info.codepoints })));
    chips.appendChild(chip(info.outlines === 'glyf' ? t('outlines_tt') : t('outlines_cff')));
    if (info.fvar) chips.appendChild(chip(t('variable_axes', { n: info.fvar.axes.length }), 'accent'));
    if (info.color.length) chips.appendChild(chip(t('color_font', { fmt: info.color.join('/') }), 'accent'));
    if (info.hinting) chips.appendChild(chip(t('hinted')));
    chips.appendChild(chip(t('src_' + (f.insp.from || f.insp.kind))));

    // Licence signal. Stated as a signal, never as permission: browsers ignore
    // fsType entirely, and the licence text is what actually governs. Saying
    // that plainly is the difference between informing a user and giving them
    // false confidence.
    if (info.os2) {
        const perm = info.os2.fsTypePermission;
        chips.appendChild(chip(t('fstype_' + perm), perm === 'restricted' ? 'warn' : perm === 'installable' ? 'ok' : ''));
        if (info.os2.noSubsetting) chips.appendChild(chip(t('fstype_no_subset'), 'warn'));
    }

    renderPreview(f, info);
    renderOptions(f, info);
    renderInspector(f, info);
}

function faceInfo(f) {
    if (!f || !f.insp) return null;
    if (f.insp.collection) {
        const face = f.insp.faces.filter((x) => x.index === f.faceIndex)[0];
        return face && face.info;
    }
    return f.insp.info;
}

/* ─────────────────────────── live preview ─────────────────────────── */

let _previewFace = null;
let _previewSeq = 0;

async function renderPreview(f, info) {
    const wrap = $('fxSample');
    const my = ++_previewSeq;
    const fam = '__fcPreview' + my;
    try {
        const bytes = f.bytes;
        const face = new FontFace(fam, bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength));
        await face.load();
        if (my !== _previewSeq) return;
        if (_previewFace) { try { document.fonts.delete(_previewFace); } catch (e) { /* gone */ } }
        document.fonts.add(face);
        _previewFace = face;
        wrap.style.fontFamily = '"' + fam + '"';
        wrap.classList.remove('is-fallback');
    } catch (e) {
        // A collection or an exotic container the browser will not load is not
        // an error — the preview simply falls back and says so.
        wrap.style.fontFamily = '';
        wrap.classList.add('is-fallback');
    }
    applyPreviewControls(info);
}

function applyPreviewControls(info) {
    const wrap = $('fxSample');
    const size = $('fxSize').value;
    wrap.style.fontSize = size + 'px';
    $('fxSizeOut').textContent = size + 'px';
    const text = $('fxText').value;
    wrap.textContent = text || t('preview_default');

    const axesBox = $('fxAxes');
    axesBox.innerHTML = '';
    if (info && info.fvar && info.fvar.axes.length) {
        axesBox.hidden = false;
        const settings = [];
        info.fvar.axes.forEach((a) => {
            const row = el('label', 'fx-axis');
            row.appendChild(el('span', 'fx-axis-tag', a.name && a.name !== a.tag ? a.name : a.tag));
            const inp = document.createElement('input');
            inp.type = 'range';
            inp.min = a.min; inp.max = a.max; inp.step = (a.max - a.min) > 20 ? 1 : 0.1;
            inp.value = (S.axisValues && S.axisValues[a.tag] !== undefined) ? S.axisValues[a.tag] : a.def;
            inp.dataset.axis = a.tag;
            const out = el('output', 'fx-axis-val', String(inp.value));
            inp.addEventListener('input', () => {
                S.axisValues = S.axisValues || {};
                S.axisValues[a.tag] = Number(inp.value);
                out.textContent = inp.value;
                applyAxes(info);
                syncInstanceFields(info);
            });
            row.appendChild(inp);
            row.appendChild(out);
            axesBox.appendChild(row);
            settings.push("'" + a.tag + "' " + inp.value);
        });
        if (info.fvar.instances.length) {
            const sel = document.createElement('select');
            sel.className = 'fx-select fx-inst';
            sel.appendChild(new Option(t('named_instance'), ''));
            info.fvar.instances.forEach((inst, i) => sel.appendChild(new Option(inst.name || ('#' + i), String(i))));
            sel.addEventListener('change', () => {
                const inst = info.fvar.instances[Number(sel.value)];
                if (!inst) return;
                S.axisValues = Object.assign({}, inst.coords);
                applyPreviewControls(info);
                syncInstanceFields(info);
            });
            axesBox.appendChild(sel);
        }
        applyAxes(info);
    } else {
        axesBox.hidden = true;
    }
}

function applyAxes(info) {
    const wrap = $('fxSample');
    if (!info.fvar) { wrap.style.fontVariationSettings = ''; return; }
    const parts = info.fvar.axes.map((a) => {
        const v = (S.axisValues && S.axisValues[a.tag] !== undefined) ? S.axisValues[a.tag] : a.def;
        return "'" + a.tag + "' " + v;
    });
    wrap.style.fontVariationSettings = parts.join(', ');
}

/* ─────────────────────────── options ─────────────────────────── */

function renderOptions(f, info) {
    $('fxOpts').hidden = false;

    // Variable-font block only exists for variable fonts.
    const varBox = $('fxVarBox');
    varBox.hidden = !info.fvar;
    if (info.fvar) syncInstanceFields(info);

    // Named subsets the font can actually serve — offering "Cyrillic" for a
    // Latin-only font is how a user ends up with an empty file.
    const subBox = $('fxSubsets');
    subBox.innerHTML = '';
    const avail = subsetAvailability(info.coverage).filter((s) => s.pct > 0.2);
    avail.forEach((s) => {
        const lab = el('label', 'fx-check');
        const cb = document.createElement('input');
        cb.type = 'checkbox'; cb.value = s.key; cb.className = 'fx-subset';
        if (s.key === 'latin') cb.checked = true;
        cb.addEventListener('change', updateEstimate);
        lab.appendChild(cb);
        lab.appendChild(el('span', '', s.label));
        lab.appendChild(el('span', 'fx-check-n', s.have + ' · ' + pct(s.pct)));
        subBox.appendChild(lab);
    });

    $('fxRenameFamily').placeholder = suggestFamilyName(info, f.name);
    updateEstimate();
}

function syncInstanceFields(info) {
    const box = $('fxInstance');
    if (!box || !info.fvar) return;
    const pinAll = $('fxPinAll').checked;
    box.textContent = info.fvar.axes.map((a) => {
        const v = (S.axisValues && S.axisValues[a.tag] !== undefined) ? S.axisValues[a.tag] : a.def;
        return a.tag + ' ' + v;
    }).join(' · ');
    $('fxPinNote').textContent = pinAll ? t('pin_all_note') : t('pin_none_note');
}

function readOpts() {
    const formats = [].slice.call(document.querySelectorAll('.fx-fmt:checked')).map((i) => i.value);
    const charset = (document.querySelector('input[name=fxCharset]:checked') || {}).value || 'all';
    const o = {
        formats: formats.length ? formats : ['woff2'],
        charset,
        features: '*',
        dropHinting: $('fxDropHint').checked,
        fixMetrics: $('fxFixMetrics').checked ? ($('fxMetricsMode').value || 'safe') : false,
    };
    if (charset === 'text') o.text = $('fxCharText').value;
    if (charset === 'subsets') {
        o.subsets = [].slice.call(document.querySelectorAll('.fx-subset:checked')).map((i) => i.value);
        // Slicing supersedes a single combined subset: each language becomes
        // its own file with its own unicode-range.
        if ($('fxSlice') && $('fxSlice').checked && o.subsets.length) o.sliceBy = o.subsets;
    }

    const info = faceInfo(S.files[S.sel]);
    if (info && info.fvar && $('fxPinAll').checked) {
        o.instance = {};
        info.fvar.axes.forEach((a) => {
            o.instance[a.tag] = (S.axisValues && S.axisValues[a.tag] !== undefined) ? S.axisValues[a.tag] : a.def;
        });
        const wght = o.instance.wght;
        if (Number.isFinite(wght)) o.style = { weight: wght, bold: wght >= 600, italic: false };
    }
    const fam = $('fxRenameFamily').value.trim();
    if (fam) o.rename = { family: fam, typoFamily: fam };
    return o;
}

/**
 * Estimated output size and time, shown BEFORE the click.
 *
 * The estimate is coarse by nature (compression depends on the outlines), so
 * it is labelled as an estimate and the time half is calibrated from this
 * device's own last run rather than a constant baked in on a desktop.
 */
function updateEstimate() {
    const f = S.files[S.sel];
    const info = faceInfo(f);
    if (!info) return;
    const o = readOpts();

    let keepRatio = 1;
    if (o.charset === 'text') {
        const chars = new Set([...(o.text || '')].map((c) => c.codePointAt(0))).size || 1;
        keepRatio = Math.min(1, Math.max(0.02, chars / Math.max(1, info.codepoints)));
    } else if (o.charset === 'subsets') {
        let ranges = [];
        for (const k of o.subsets || []) if (SUBSETS[k]) ranges = ranges.concat(SUBSETS[k].ranges);
        const keep = intersectRanges(normalizeRanges(ranges), info.coverage);
        let n = 0;
        for (const [a, b] of keep) n += b - a + 1;
        keepRatio = Math.min(1, Math.max(0.02, n / Math.max(1, info.codepoints)));
    }
    // Glyph data dominates but tables do not shrink linearly; 0.55 exponent
    // matches the measured Arial curve (4651→155 glyphs was 1021K→35K, not
    // 1021K×0.03).
    const est = f.size * Math.pow(keepRatio, 0.55) * (o.dropHinting ? 0.75 : 1);
    const w2 = est * 0.42;
    const secs = Math.max(0.3, (est / 1024) / S.kbps);

    $('fxEstimate').textContent = t('estimate', {
        size: fmtBytes(Math.round(o.formats.indexOf('woff2') >= 0 ? w2 : est)),
        time: secs < 1.5 ? t('time_instant') : t('time_seconds', { n: Math.ceil(secs) }),
    });
}

/* ─────────────────────────── convert ─────────────────────────── */

async function runConvert() {
    if (S.busy) return;
    const usable = S.files.filter((f) => !f.error && f.insp);
    if (!usable.length) return;

    // Unit = FILE, keyed per file, so a batch of six spends six and re-running
    // the same file the same day spends nothing.
    const keys = usable.map((f) => f.name + ':' + f.size);
    const run = window.__toolLimit ? window.__toolLimit.begin('font-converter', keys) : { commit() { }, abort() { } };
    if (!run) return;

    S.busy = true;
    _themeRedraws.length = 0;          // the previous proof's canvases are gone
    $('fxGo').disabled = true;
    $('fxProg').hidden = false;
    $('fxResult').hidden = true;
    window.__stage && window.__stage('convert_start');

    const opts = readOpts();
    const started = performance.now();
    let totalSrc = 0;
    let ok = 0;

    try {
        for (let i = 0; i < usable.length; i++) {
            const f = usable[i];
            totalSrc += f.size;
            setProgress(i / usable.length, t('st_converting', { name: f.name, i: i + 1, n: usable.length }));
            const r = await ask(
                'convert',
                { bytes: f.bytes.buffer.slice(0), opts: Object.assign({ faceIndex: f.faceIndex }, opts) },
                (stage, ratio) => setProgress((i + (ratio || 0) * 0.9) / usable.length, t('st_' + String(stage).split(':')[0]) + ' · ' + f.name),
            );
            f.result = r;
            f.outputs = r.outputs.map((o) => ({
                format: o.format, ext: o.ext, mime: o.mime, renamedExt: o.renamedExt,
                slice: o.slice || null,
                bytes: new Uint8Array(o.buffer),
            }));
            // Re-attach each slice to its outputs on this side: the worker sent
            // the bytes once (in `outputs`) and the metadata once, so the join
            // happens here rather than shipping the payload twice.
            f.result.slices = (r.slices || []).map((s) => Object.assign({}, s, {
                outputs: f.outputs.filter((o) => o.slice === s.key),
            })).filter((s) => s.outputs.length);
            ok++;
        }

        // Calibrate the ETA from what actually happened here.
        const secs = (performance.now() - started) / 1000;
        if (secs > 0.4 && totalSrc > 40960 && opts.formats.indexOf('woff2') >= 0) {
            S.kbps = Math.round((totalSrc / 1024) / secs);
            try { localStorage.setItem('fc_woff2_kbps', String(S.kbps)); } catch (e) { /* private mode */ }
        }

        setProgress(1, t('st_done'));
        await renderResults(usable);
        run.commit();
        window.__stage && window.__stage('convert_done');
        window.__aeToolComplete && window.__aeToolComplete({
            files: ok, formats: opts.formats.join('+'), charset: opts.charset,
            subset: opts.charset !== 'all', instanced: !!opts.instance, fixed_metrics: !!opts.fixMetrics,
        });
    } catch (e) {
        run.abort();
        window.__handleError && window.__handleError(e, 'font-converter');
        window.__aeCritical && window.__aeCritical('convert_failed', { code: (e && e.code) || 'unknown' });
        window.__toast(t('err_' + String((e && e.code) || 'convert_failed').toLowerCase()) || t('err_convert_failed'), 'error');
    } finally {
        S.busy = false;
        $('fxGo').disabled = false;
        $('fxProg').hidden = true;
    }
}

function setProgress(ratio, label) {
    $('fxProgFill').style.width = Math.round(Math.max(0, Math.min(1, ratio)) * 100) + '%';
    $('fxProgTxt').textContent = label || '';
}

/* ─────────────────────────── results ─────────────────────────── */

async function renderResults(files) {
    const box = $('fxResult');
    box.hidden = false;
    const list = $('fxOutputs');
    list.innerHTML = '';

    for (const f of files) {
        const card = el('div', 'fx-out');
        const head = el('div', 'fx-out-head');
        head.appendChild(el('strong', '', f.result.after.names.family || f.name));
        const d = f.result.diff;
        head.appendChild(el('span', 'fx-out-meta',
            t('glyphs_kept', { a: d.glyphsAfter, b: d.glyphsBefore })));
        card.appendChild(head);

        const row = el('div', 'fx-dl-row');
        const sliceRanges = {};
        for (const s of (f.result.slices || [])) sliceRanges[s.key] = s;
        f.outputs.forEach((o) => {
            const btn = el('button', 'fx-dl');
            btn.type = 'button';
            const sl = o.slice ? sliceRanges[o.slice] : null;
            btn.appendChild(el('span', 'fx-dl-fmt', o.ext.toUpperCase() + (sl ? ' · ' + sl.label : '')));
            // A sliced download is only useful next to the range it serves —
            // otherwise the user has four files and no idea which is which.
            btn.appendChild(el('span', 'fx-dl-size', fmtBytes(o.bytes.length)
                + (o.slice ? '' : ' ' + delta(f.size, o.bytes.length))));
            if (sl) btn.appendChild(el('span', 'fx-dl-range', rangesToCss(sl.ranges)));
            btn.addEventListener('click', () => {
                window.__downloadFile(new Blob([o.bytes], { type: o.mime }), outName(f, o));
            });
            row.appendChild(btn);
            if (o.renamedExt) {
                // Asked for .ttf, got CFF outlines. Saying so is the whole
                // difference between this tool and the ones that rename the
                // file and let you find out in a validator.
                row.appendChild(el('span', 'fx-note', t('ext_kept', { asked: o.renamedExt.asked.toUpperCase(), got: o.renamedExt.got.toUpperCase() })));
            }
        });
        card.appendChild(row);

        card.appendChild(renderDiff(f));
        list.appendChild(card);

        // Proof runs per file but only for the first two in a batch — three
        // canvases × three sizes × N files is real memory, and the point is
        // made by the files the user actually looks at.
        if (files.indexOf(f) < 2) {
            const proofBox = el('div', 'fx-proof');
            card.appendChild(proofBox);
            renderProofInto(proofBox, f).catch(() => { proofBox.remove(); });
        }
    }

    renderKit(files);
}

function outName(f, o) {
    const info = f.result ? f.result.after : null;
    const base = (info && (info.names.postScriptName || info.names.family)) || f.name.replace(/\.[a-z0-9]+$/i, '');
    const clean = String(base).replace(/[^\w.-]+/g, '-').replace(/^-+|-+$/g, '');
    // A slice has to be identifiable in the folder AND in the stylesheet:
    // three files called Inter.woff2 are indistinguishable once downloaded.
    return clean + (o.slice ? '-' + o.slice : '') + '.' + o.ext;
}

function renderDiff(f) {
    const d = f.result.diff;
    const box = el('div', 'fx-diff');

    const add = (label, before, after, good) => {
        const r = el('div', 'fx-diff-row');
        r.appendChild(el('span', 'fx-diff-k', label));
        r.appendChild(el('span', 'fx-diff-v', before + ' → ' + after));
        if (good) r.classList.add('is-good');
        box.appendChild(r);
    };

    if (f.result.slices && f.result.slices.length) {
        const r = el('div', 'fx-diff-row is-good');
        r.appendChild(el('span', 'fx-diff-k', t('d_slices')));
        r.appendChild(el('span', 'fx-diff-v', f.result.slices
            .map((s) => s.label + ' ' + fmtBytes(s.outputs[0].bytes.length)).join(' · ')));
        box.appendChild(r);
    }
    add(t('d_chars'), d.codepointsBefore, d.codepointsAfter);
    add(t('d_glyphs'), d.glyphsBefore, d.glyphsAfter);
    if (d.hintingBefore !== d.hintingAfter) add(t('d_hinting'), t(d.hintingBefore ? 'yes' : 'no'), t(d.hintingAfter ? 'yes' : 'no'));
    if (d.variableBefore !== d.variableAfter) add(t('d_variable'), t('yes'), t('no'), true);

    if (f.result.metricsChange && f.result.metricsChange.changed) {
        const m = f.result.metricsChange;
        const r = el('div', 'fx-diff-row is-good');
        r.appendChild(el('span', 'fx-diff-k', t('d_metrics')));
        r.appendChild(el('span', 'fx-diff-v', t('metrics_fixed', {
            asc: m.after.hhea.ascent, desc: m.after.hhea.descent, lh: m.lineHeight.toFixed(3),
        })));
        box.appendChild(r);
    }

    if (d.tablesDropped.length) {
        const r = el('div', 'fx-diff-row');
        r.appendChild(el('span', 'fx-diff-k', t('d_tables_dropped')));
        r.appendChild(el('span', 'fx-diff-v', d.tablesDropped.join(', ') + (d.tablesDropped.indexOf('DSIG') >= 0 ? ' — ' + t('dsig_note') : '')));
        box.appendChild(r);
    }

    // Lost features, each with its reason. An unexplained loss is the only one
    // painted as a warning; the rest are consequences the user chose.
    if (d.featuresLost.length) {
        const grouped = { 'script-dropped': [], 'no-glyphs': [], unexplained: [] };
        d.featuresLost.forEach((x) => (grouped[x.verdict] || grouped.unexplained).push(x));
        for (const key of ['unexplained', 'script-dropped', 'no-glyphs']) {
            const g = grouped[key];
            if (!g.length) continue;
            const r = el('div', 'fx-diff-row' + (key === 'unexplained' ? ' is-warn' : ''));
            r.appendChild(el('span', 'fx-diff-k', t('d_features_' + key.replace('-', '_'))));
            const scripts = [...new Set(g.reduce((acc, x) => acc.concat(x.scripts), []))];
            r.appendChild(el('span', 'fx-diff-v',
                g.map((x) => x.tag).join(', ') + (key === 'script-dropped' && scripts.length ? ' (' + scripts.join(', ') + ')' : '')));
            box.appendChild(r);
        }
    }
    return box;
}

async function renderProofInto(box, f) {
    box.appendChild(el('div', 'fx-proof-head', t('proof_running')));
    const info = f.result.after;
    const source = f.bytes;

    // Compare against the ORIGINAL file as the browser loads it. If the source
    // is a collection the browser cannot load it directly, so the proof is
    // skipped rather than faked.
    const orig = f.insp.collection ? null : source;
    if (!orig) { box.remove(); return; }

    /* With slicing there are several outputs and none of them is "the" result.
     * Prove the BIGGEST slice — it carries the most glyphs, so it is the most
     * informative comparison — and say which one it is, because an unlabelled
     * proof of a Latin cut would look like the whole font passed. */
    const slices = f.result.slices || [];
    let proofSlice = null;
    if (slices.length) {
        proofSlice = slices.slice().sort((a, b) => b.outputs[0].bytes.length - a.outputs[0].bytes.length)[0];
    }
    const pool = proofSlice ? proofSlice.outputs : f.outputs;
    const converted = pool.filter((o) => o.format === 'woff2')[0] || pool[0];
    const check = await loadable(converted.bytes);
    if (!check.ok) {
        box.innerHTML = '';
        box.appendChild(el('div', 'fx-proof-bad', t('proof_unloadable')));
        window.__aeCritical && window.__aeCritical('output_unloadable', { error: check.error });
        return;
    }

    const r = await renderProof(orig, converted.bytes, {
        coverage: (proofSlice ? proofSlice.info : info).coverage,
        text: $('fxCharText').value && readOpts().charset === 'text' ? $('fxCharText').value : '',
    });
    box.innerHTML = '';
    if (r.unsupported || r.skipped) { box.remove(); return; }

    const head = el('div', 'fx-proof-head' + (r.identical ? ' is-ok' : ' is-warn'));
    head.textContent = (r.identical
        ? t('proof_identical')
        : t('proof_differs', { pct: r.worst.diffPct.toFixed(2), px: r.worst.px }))
        + (proofSlice ? ' ' + t('slice_of', { slice: proofSlice.label }) : '');
    box.appendChild(head);

    const strip = el('div', 'fx-proof-strip');
    const shown = r.results.filter((x) => x.px === (r.identical ? 28 : r.worst.px))[0] || r.results[1] || r.results[0];
    const mk = (canvas, label) => {
        const cell = el('div', 'fx-proof-cell');
        cell.appendChild(el('span', 'fx-proof-lbl', label));
        canvas.className = 'fx-proof-canvas';
        cell.appendChild(canvas);
        return cell;
    };
    strip.appendChild(mk(shown.canvasA, t('proof_original')));
    strip.appendChild(mk(shown.canvasB, t('proof_converted')));
    if (!r.identical) strip.appendChild(mk(shown.canvasDiff, t('proof_diff')));
    box.appendChild(strip);
    box.appendChild(el('div', 'fx-proof-note', t('proof_note')));

    // The specimen is a canvas, so it does not inherit a theme switch the way
    // the rest of the page does — repaint it when the theme attribute moves.
    if (typeof r.redraw === 'function') watchTheme(r.redraw);
}

let _themeObserver = null;
const _themeRedraws = [];

function watchTheme(fn) {
    _themeRedraws.push(fn);
    if (_themeObserver) return;
    _themeObserver = new MutationObserver(() => {
        for (const f of _themeRedraws) { try { f(); } catch (e) { /* a stale proof is not an error */ } }
    });
    _themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
}

/* ─────────────────────────── CSS kit ─────────────────────────── */

function buildKit(files) {
    const faces = [];
    for (const f of files) {
        if (!f.outputs) continue;
        const info = f.result.after;
        const family = ($('fxCssFamily').value.trim() || suggestFamilyName(info, f.name));
        if (f.result.slices && f.result.slices.length) {
            // One rule per slice. The browser then downloads a cut only when a
            // character from its range actually appears on the page — which is
            // the entire point of slicing and is lost if they share a rule.
            for (const sl of f.result.slices) {
                faces.push({
                    family,
                    info: sl.info,
                    unicodeRange: sl.ranges,
                    files: sl.outputs.map((o) => ({ format: o.format, name: outName(f, o) })),
                    display: $('fxDisplay').value,
                });
            }
            continue;
        }
        faces.push({
            family,
            info,
            files: f.outputs.filter((o) => o.format !== 'ttf' || files.length === 1)
                .map((o) => ({ format: o.format, name: outName(f, o) })),
            display: $('fxDisplay').value,
        });
    }
    const family = faces.length ? faces[0].family : 'MyFont';
    return { family, faces, cssName: 'fonts.css' };
}

function renderKit(files) {
    const kit = buildKit(files);
    const css = buildStylesheet(kit);
    $('fxCss').value = css;
    $('fxKitBox').hidden = false;

    $('fxCopyCss').onclick = async () => {
        try {
            await navigator.clipboard.writeText($('fxCss').value);
            window.__toast(t('css_copied'), 'success');
        } catch (e) {
            $('fxCss').select();
            window.__toast(t('css_copy_manual'), 'info');
        }
    };

    $('fxZip').onclick = async () => {
        try {
            $('fxZip').disabled = true;
            const items = [];
            for (const f of files) {
                for (const o of f.outputs) items.push({ name: outName(f, o), blob: new Blob([o.bytes], { type: o.mime }) });
            }
            items.push({ name: 'fonts.css', blob: new Blob([$('fxCss').value], { type: 'text/css' }) });
            items.push({ name: 'demo.html', blob: new Blob([buildDemoHtml(kit)], { type: 'text/html' }) });
            const zip = await pack(items);
            window.__downloadFile(zip, (kit.family || 'webfont').replace(/[^\w.-]+/g, '-') + '-kit.zip');
        } catch (e) {
            window.__toast(t(e && e.code === 'ZIP_TOO_BIG' ? 'err_zip_too_big' : 'err_zip'), 'error');
        } finally {
            $('fxZip').disabled = false;
        }
    };
}

/* ─────────────────────────── inspector ─────────────────────────── */

function renderInspector(f, info) {
    const box = $('fxInspectBody');
    box.innerHTML = '';

    const section = (title) => {
        const s = el('section', 'fx-insp-sec');
        s.appendChild(el('h4', '', title));
        box.appendChild(s);
        return s;
    };

    /* languages */
    const langs = languageCoverage(info.coverage);
    if (langs.length) {
        const s = section(t('insp_languages'));
        const wrap = el('div', 'fx-lang-grid');
        langs.forEach((l) => {
            const c = el('span', 'fx-lang' + (l.pct === 1 ? ' is-full' : ' is-part'));
            c.textContent = l.label + (l.pct === 1 ? '' : ' ' + pct(l.pct));
            if (l.pct < 1) c.title = t('insp_missing') + ': ' + l.missingChars.join(' ');
            wrap.appendChild(c);
        });
        s.appendChild(wrap);
        s.appendChild(el('p', 'fx-insp-note', t('insp_languages_note')));
    }

    /* scripts */
    const scripts = scriptCoverage(info.coverage);
    if (scripts.length) {
        const s = section(t('insp_scripts'));
        s.appendChild(el('p', 'fx-insp-line', scripts.map((x) => x.label + ' ' + pct(x.pct)).join(' · ')));
    }

    /* OpenType features, with live samples */
    if (info.gsub.length || info.gpos.length) {
        const s = section(t('insp_features'));
        const grid = el('div', 'fx-feat-grid');
        const all = [...new Set(info.gsub.concat(info.gpos))].sort();
        all.forEach((tag) => {
            const cell = el('div', 'fx-feat');
            cell.appendChild(el('code', '', tag));
            const label = t('feat_' + tag);
            cell.appendChild(el('span', 'fx-feat-name', label === 'feat_' + tag ? '' : label));
            const scripts2 = info.featureScripts && info.featureScripts[tag];
            if (scripts2 && scripts2.length) cell.title = scripts2.join(', ');
            grid.appendChild(cell);
        });
        s.appendChild(grid);
    }

    /* vertical metrics */
    const m = f.insp.metrics;
    if (m) {
        const s = section(t('insp_metrics'));
        const tbl = el('table', 'fx-mtbl');
        const head = el('tr');
        [t('m_source'), t('m_ascent'), t('m_descent'), t('m_gap'), t('m_lineheight')].forEach((h) => head.appendChild(el('th', '', h)));
        tbl.appendChild(head);
        const rows = [['hhea', m.hhea, m.lineHeight.hhea], ['OS/2 typo', m.typo, m.lineHeight.typo], ['OS/2 win', m.win, m.lineHeight.win]];
        rows.forEach(([label, p, lhv]) => {
            if (!p) return;
            const r = el('tr');
            r.appendChild(el('td', '', label));
            r.appendChild(el('td', '', String(p.ascent)));
            r.appendChild(el('td', '', String(p.descent)));
            r.appendChild(el('td', '', String(p.lineGap)));
            r.appendChild(el('td', '', lhv ? lhv.toFixed(3) : '—'));
            tbl.appendChild(r);
        });
        s.appendChild(tbl);

        const verdict = el('p', 'fx-verdict ' + (m.consistent ? 'is-ok' : 'is-warn'));
        verdict.textContent = m.consistent
            ? t('metrics_ok')
            : t('metrics_bad', {
                mac: m.effective.mac.toFixed(3), win: m.effective.windows.toFixed(3),
                android: m.effective.android.toFixed(3), spread: (m.spread * 100).toFixed(1),
            });
        s.appendChild(verdict);
        if (m.clipsOnWindows) s.appendChild(el('p', 'fx-verdict is-warn', t('metrics_clip')));
        if (!m.consistent) {
            const b = el('button', 'fx-linkbtn', t('metrics_fix_cta'));
            b.type = 'button';
            b.addEventListener('click', () => {
                $('fxFixMetrics').checked = true;
                $('fxFixMetrics').scrollIntoView({ block: 'nearest' });
                updateEstimate();
            });
            s.appendChild(b);
        }
    }

    /* name records + tables */
    const s2 = section(t('insp_names'));
    const dl = el('dl', 'fx-names');
    const order = ['family', 'subfamily', 'typoFamily', 'typoSubfamily', 'fullName', 'postScriptName', 'version', 'designer', 'manufacturer', 'copyright', 'license', 'licenseUrl', 'vendorUrl'];
    order.forEach((k) => {
        if (!info.names[k]) return;
        dl.appendChild(el('dt', '', t('name_' + k) === 'name_' + k ? k : t('name_' + k)));
        const dd = el('dd', '');
        if (/^https?:/i.test(info.names[k])) {
            const a = el('a', '', info.names[k]);
            a.href = info.names[k]; a.target = '_blank'; a.rel = 'noopener nofollow';
            dd.appendChild(a);
        } else dd.textContent = info.names[k];
        dl.appendChild(dd);
    });
    s2.appendChild(dl);

    const s3 = section(t('insp_tables', { n: info.tableCount }));
    s3.appendChild(el('p', 'fx-insp-line fx-mono', info.tableTags.join(' ')));
}

/* ─────────────────────────── wiring ─────────────────────────── */

function render() {
    renderList();
    renderCard(S.files[S.sel]);
}

function boot() {
    const drop = $('fxDrop');
    if (!drop) return;

    drop.addEventListener('tool-uploader:file', (e) => {
        const d = e.detail || {};
        addFiles(d.files && d.files.length ? d.files : [d.file]).catch((err) => {
            window.__handleError && window.__handleError(err, 'font-converter/add');
        });
    });
    drop.addEventListener('tool-uploader:clear', () => {
        S.files = []; S.sel = 0;
        $('fxCard').hidden = true; $('fxOpts').hidden = true;
        $('fxResult').hidden = true; $('fxList').hidden = true;
        $('fxKitBox').hidden = true;
    });

    $('fxGo').addEventListener('click', runConvert);
    $('fxSize').addEventListener('input', () => applyPreviewControls(faceInfo(S.files[S.sel])));
    $('fxText').addEventListener('input', () => applyPreviewControls(faceInfo(S.files[S.sel])));
    $('fxPinAll').addEventListener('change', () => { syncInstanceFields(faceInfo(S.files[S.sel])); updateEstimate(); });

    const syncCharset = () => {
        const picked = (document.querySelector('input[name=fxCharset]:checked') || {}).value || 'all';
        $('fxCharTextRow').hidden = picked !== 'text';
        $('fxSubsets').hidden = picked !== 'subsets';
        $('fxSliceRow').hidden = picked !== 'subsets';
        updateEstimate();
    };
    document.querySelectorAll('input[name=fxCharset]').forEach((r) => r.addEventListener('change', syncCharset));
    syncCharset();
    ['fxCharText', 'fxDropHint', 'fxFixMetrics', 'fxMetricsMode', 'fxSlice'].forEach((id) => {
        const n = $(id);
        if (n) n.addEventListener('input', updateEstimate);
    });
    document.querySelectorAll('.fx-fmt').forEach((c) => c.addEventListener('change', () => {
        updateEstimate();
        // Warm the encoder the moment WOFF2 is on the menu: the wasm is 751 KB
        // and fetching it during the click is the whole perceived latency.
        if (c.value === 'woff2' && c.checked) ask('prewarm', { lane: 'woff2' }).catch(() => { });
    }));

    // Settings that should survive a reload — the shared primitive, not
    // hand-rolled localStorage.
    if (window.__toolSettings) {
        window.__toolSettings.bind('font-converter', $('fxDisplay'), 'display', 'swap');
        window.__toolSettings.bind('font-converter', $('fxText'), 'preview_text', '');
        window.__toolSettings.bind('font-converter', $('fxSize'), 'preview_size', '40');
    }
    window.__stage && window.__stage('page_ready');
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
